// src/components/lessons/LessonContentRenderer.jsx
// Main component that renders lesson content in progressive sections
// Design: Black, White, Royal Blue (#3A86FF)
// Layout: Left sidebar with tabs + progress bar (desktop) / Top tabs (mobile)

import React, { useState, useEffect, useMemo } from 'react';
import { ChevronRight, Check, ChevronDown, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Import content renderers
// ... (Keep all renderer imports)
import ParagraphRenderer from './renderers/ParagraphRenderer';
import HeaderRenderer from './renderers/HeaderRenderer';
import ExamFactsRenderer from './renderers/ExamFactsRenderer';
import ChecklistRenderer from './renderers/ChecklistRenderer';
import OrderedListRenderer from './renderers/OrderedListRenderer';
import UnorderedListRenderer from './renderers/UnorderedListRenderer';
import TipRenderer from './renderers/TipRenderer';
import StrategyStepsRenderer from './renderers/StrategyStepsRenderer';
import ScenarioRenderer from './renderers/ScenarioRenderer';
import AccordionRenderer from './renderers/AccordionRenderer';
import CodeBlockRenderer from './renderers/CodeBlockRenderer';
import SectionRenderer from './renderers/SectionRenderer';
import ListRenderer from './renderers/ListRenderer';
import TableRenderer from './renderers/TableRenderer';
import WarningListRenderer from './renderers/WarningListRenderer';
import TipListRenderer from './renderers/TipListRenderer';
import StepByStepRenderer from './renderers/StepByStepRenderer';
import TrapRenderer from './renderers/TrapRenderer';
import ClozeTextRenderer from './renderers/ClozeTextRenderer';
import OutlineRenderer from './renderers/OutlineRenderer';
import FlashcardsRenderer from './renderers/FlashcardsRenderer';
import TipsGridRenderer from './renderers/TipsGridRenderer';
import ExampleRenderer from './renderers/ExampleRenderer';
import WarningRenderer from './renderers/WarningRenderer';
import VideoRenderer from './renderers/VideoRenderer';
import ExamSectionRenderer from './renderers/ExamSectionRenderer';

const LessonContentRenderer = ({
  contentBlocks,
  onComplete,
  initialProgress = {},
  controlledSection,
  onSectionChange,
  onOpenSectionsMenuRef,
}) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [internalSection, setInternalSection] = useState(0);
  const [internalExpanded, setInternalExpanded] = useState(0);
  const [isSectionsMenuOpen, setIsSectionsMenuOpen] = useState(false);
  const [isSectionsMenuClosing, setIsSectionsMenuClosing] = useState(false);
  const isControlled = controlledSection !== undefined && onSectionChange;
  const currentSection = isControlled ? controlledSection : internalSection;
  const expandedSection = isControlled ? controlledSection : internalExpanded;
  const [completedSections, setCompletedSections] = useState(new Set());

  // ... (Keep useEffect hooks for responsive, auto-complete) ...
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Expose sections menu open function to parent (for bottom nav "Sections" button)
  useEffect(() => {
    if (onOpenSectionsMenuRef) {
      onOpenSectionsMenuRef.current = () => {
        setIsSectionsMenuClosing(false);
        setIsSectionsMenuOpen(true);
      };
    }
  }, [onOpenSectionsMenuRef]);

  // Close handler: play slide-out then hide
  const closeSectionsMenu = () => {
    if (isSectionsMenuClosing) return;
    setIsSectionsMenuClosing(true);
  };

  useEffect(() => {
    if (!isSectionsMenuClosing) return;
    const id = setTimeout(() => {
      setIsSectionsMenuOpen(false);
      setIsSectionsMenuClosing(false);
    }, 320);
    return () => clearTimeout(id);
  }, [isSectionsMenuClosing]);

  useEffect(() => {
    const newCompleted = new Set();
    for (let i = 0; i < currentSection; i++) {
      newCompleted.add(i);
    }
    setCompletedSections(newCompleted);
  }, [currentSection]);

  // ... (Keep helper functions: getBlockTitle, getBestTitle) ...
  const getBlockTitle = (block, fallbackNumber) => {
    // Priority 1: Explicit title field
    if (block.title) return block.title;

    // Priority 2: Text from header/heading blocks
    if ((block.type === 'header' || block.type === 'heading') && block.text) {
      return block.text;
    }

    // Priority 3: Use block type as readable title (NO translation keys)
    const typeToTitle = {
      'strategy_steps': 'Strategy Steps',
      'exam_section': 'Exam Section',
      'exam_facts': 'Exam Facts',
      'scenario': 'Scenario',
      'tip': 'Tips & Tricks',
      'tip_list': 'Pro Tips',
      'checklist': 'Checklist',
      'ordered_list': 'Step by Step',
      'unordered_list': 'Key Points',
      'warning_list': 'Important Warnings',
      'table': 'Reference Table',
      'step_by_step': 'Step-by-Step Guide',
      'trap': 'Common Traps'
    };

    if (typeToTitle[block.type]) {
      return typeToTitle[block.type];
    }

    // Priority 4: Generic section number
    return `Section ${fallbackNumber}`;
  };

  const getBestTitle = (blocks, fallbackNumber) => {
    // Try to find a header or heading
    const header = blocks.find(b => b.type === 'header' || b.type === 'heading');
    if (header) return getBlockTitle(header, fallbackNumber);

    // Try to find any block with a title
    const blockWithTitle = blocks.find(b => b.title);
    if (blockWithTitle) return getBlockTitle(blockWithTitle, fallbackNumber);

    // Use the first block's type
    if (blocks.length > 0) {
      return getBlockTitle(blocks[0], fallbackNumber);
    }

    return `Section ${fallbackNumber}`;
  };

  // ... (Keep sections calculation logic) ...
  const sections = useMemo(() => {
    if (!contentBlocks || contentBlocks.length === 0) return [];

    console.log('📦 LessonContentRenderer - Processing content blocks:', {
      totalBlocks: contentBlocks.length,
      blockTypes: contentBlocks.map(b => b.type)
    });

    const sectionList = [];
    let currentGroup = [];
    let currentTitle = null;
    let sectionCount = 0;

    contentBlocks.forEach((block, index) => {
      // Headers (ANY level) ALWAYS create new sections
      // 'heading' is an alias for 'header'
      if (block.type === 'header' || block.type === 'heading') {
        // Save previous group if exists
        if (currentGroup.length > 0) {
          sectionList.push({
            title: currentTitle || getBestTitle(currentGroup, sectionCount + 1),
            blocks: currentGroup
          });
          sectionCount++;
        }
        // Start new group with this header as the title
        currentTitle = block.text;
        currentGroup = [];
      }
      // These block types ALSO create new sections
      else if (
        block.type === 'strategy_steps' ||
        block.type === 'exam_section' ||
        block.type === 'exam_facts' ||
        block.type === 'scenario'
      ) {
        // Save previous group if exists
        if (currentGroup.length > 0) {
          sectionList.push({
            title: currentTitle || getBestTitle(currentGroup, sectionCount + 1),
            blocks: currentGroup
          });
          sectionCount++;
          currentTitle = null;
          currentGroup = [];
        }
        // Create a section for this special block
        sectionList.push({
          title: getBlockTitle(block, sectionCount + 1),
          blocks: [block]
        });
        sectionCount++;
        currentTitle = null;
        currentGroup = [];
      }
      else {
        // Add block to current group
        currentGroup.push(block);
      }
    });

    // Add final group
    if (currentGroup.length > 0) {
      sectionList.push({
        title: currentTitle || getBestTitle(currentGroup, sectionCount + 1),
        blocks: currentGroup
      });
    }

    // FORCE MULTIPLE SECTIONS: If still only 1 section, aggressively split
    if (sectionList.length <= 1 && contentBlocks.length > 3) {
      console.warn('⚠️ Only 1 section detected, forcing split into multiple sections');
      sectionList.length = 0; // Clear

      // Calculate optimal chunk size (aim for 3-4 sections)
      const targetSections = Math.min(Math.ceil(contentBlocks.length / 3), 6);
      const chunkSize = Math.ceil(contentBlocks.length / targetSections);

      for (let i = 0; i < contentBlocks.length; i += chunkSize) {
        const chunk = contentBlocks.slice(i, i + chunkSize);

        sectionList.push({
          title: getBestTitle(chunk, Math.floor(i / chunkSize) + 1),
          blocks: chunk
        });
      }
    }

    console.log('✅ LessonContentRenderer - Sections created:', {
      totalSections: sectionList.length,
      sectionTitles: sectionList.map(s => s.title),
      blocksPerSection: sectionList.map(s => s.blocks.length)
    });

    return sectionList;
  }, [contentBlocks]);

  // When controlled, report section count to parent when sections are first ready (must run after sections is defined)
  const reportedTotalRef = React.useRef(0);
  useEffect(() => {
    if (!isControlled || !onSectionChange || !sections?.length) return;
    if (reportedTotalRef.current !== sections.length) {
      reportedTotalRef.current = sections.length;
      onSectionChange(Math.min(controlledSection, sections.length - 1), sections.length);
    }
  }, [isControlled, onSectionChange, sections, sections?.length, controlledSection]);

 const AccordionWrapper = ({ block, isMobile, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: '0.75rem',
          border: '2px solid #000000',
          marginBottom: '1.5rem',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isHovered ? '0 8px 24px rgba(0, 0, 0, 0.12)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Accordion Header */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '100%',
            padding: isMobile ? '1rem 1.25rem' : '1.25rem 1.5rem',
            textAlign: 'left',
            fontWeight: '700',
            color: isOpen ? '#FFFFFF' : '#000000',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: isOpen ? '#000000' : '#FFFFFF',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            fontSize: isMobile ? '0.95rem' : '1.05rem',
            position: 'relative'
          }}
        >
          {/* Blue accent line when open */}
          {isOpen && (
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              background: '#3A86FF'
            }} />
          )}

          <span style={{ flex: 1, paddingRight: '1rem', paddingLeft: isOpen ? '0.5rem' : 0 }}>
            {block.title}
          </span>

          {isOpen ? (
            <ChevronDown style={{
              width: '20px',
              height: '20px',
              flexShrink: 0,
              color: '#3A86FF',
              transition: 'transform 0.3s ease'
            }} />
          ) : (
            <ChevronRight style={{
              width: '20px',
              height: '20px',
              flexShrink: 0,
              color: '#000000',
              transition: 'transform 0.3s ease'
            }} />
          )}
        </button>

        {/* Accordion Content - Render nested blocks */}
        {isOpen && (
          <div style={{
            padding: isMobile ? '1.25rem' : '1.5rem',
            borderTop: '2px solid #000000',
            background: '#FAFBFC'
          }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  // Accordion bodies are authored either as nested content blocks or, in the
  // TELC C1 lessons, as a plain string / label-to-text map under `items`.
  const AccordionBody = ({ body, isMobile }) => {
    const markup = (value) => ({
      __html: String(value).replace(
        /\*\*(.*?)\*\*/g,
        '<strong style="font-weight: 600; color: #000000;">$1</strong>'
      )
    });

    const textStyle = {
      color: '#000000',
      fontSize: isMobile ? '0.9rem' : '0.95rem',
      margin: 0,
      lineHeight: '1.6'
    };

    if (typeof body === 'string') {
      return <p style={{ ...textStyle, opacity: 0.8 }} dangerouslySetInnerHTML={markup(body)} />;
    }

    if (Array.isArray(body)) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          {body.map((entry, i) => (
            <p key={i} style={{ ...textStyle, opacity: 0.8 }} dangerouslySetInnerHTML={markup(entry)} />
          ))}
        </div>
      );
    }

    if (body && typeof body === 'object') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          {Object.entries(body).map(([label, value]) => (
            <p key={label} style={textStyle}>
              <strong style={{ fontWeight: '700', color: '#000000', marginRight: '0.5rem' }}>
                {label}:
              </strong>
              <span style={{ opacity: 0.8 }} dangerouslySetInnerHTML={markup(value)} />
            </p>
          ))}
        </div>
      );
    }

    return null;
  };

  const renderBlock = (block, index) => {
    const key = `${block.type}-${index}`;

    try {
      switch (block.type) {
        case 'paragraph':
          return <ParagraphRenderer key={key} block={block} isMobile={isMobile} />;
        case 'header':
        case 'heading': // Alias for header
          return <HeaderRenderer key={key} block={block} isMobile={isMobile} />;
        case 'exam_facts':
          return <ExamFactsRenderer key={key} block={block} isMobile={isMobile} />;
        case 'checklist':
          return <ChecklistRenderer key={key} block={block} isMobile={isMobile} />;
        case 'ordered_list':
          return <OrderedListRenderer key={key} block={block} isMobile={isMobile} />;
        case 'unordered_list':
          return <UnorderedListRenderer key={key} block={block} isMobile={isMobile} />;
        case 'list': // Generic list
          return <ListRenderer key={key} block={block} isMobile={isMobile} />;
        case 'tip':
          return <TipRenderer key={key} block={block} isMobile={isMobile} />;
        case 'strategy_steps':
          return <StrategyStepsRenderer key={key} block={block} isMobile={isMobile} />;
        case 'scenario':
          return <ScenarioRenderer key={key} block={block} isMobile={isMobile} />;
        case 'section':
          return <AccordionRenderer key={key} block={block} isMobile={isMobile} />;
        case 'code_block':
        case 'code': // Alias for code_block
          return <CodeBlockRenderer key={key} block={block} isMobile={isMobile} />;
        case 'table':
          return <TableRenderer key={key} block={block} isMobile={isMobile} />;
        case 'warning_list':
          return <WarningListRenderer key={key} block={block} isMobile={isMobile} />;
        case 'tip_list':
          return <TipListRenderer key={key} block={block} isMobile={isMobile} />;
        case 'step_by_step':
          return <StepByStepRenderer key={key} block={block} isMobile={isMobile} />;
        case 'trap':
          return <TrapRenderer key={key} block={block} isMobile={isMobile} />;
        case 'exam_section':
  return <ExamSectionRenderer key={key} block={block} isMobile={isMobile} />;
        case 'accordion': {
          const body = block.content !== undefined ? block.content : block.items;
          const isNestedBlocks =
            Array.isArray(body) && body.some((entry) => entry && typeof entry === 'object' && entry.type);

          return (
            <AccordionWrapper key={key} block={block} isMobile={isMobile}>
              {isNestedBlocks
                ? body.map((nestedBlock, nestedIndex) =>
                    renderBlock(nestedBlock, `${index}-${nestedIndex}`)
                  )
                : <AccordionBody body={body} isMobile={isMobile} />}
            </AccordionWrapper>
          );
        }

      case 'procedure':
        return <OrderedListRenderer key={key} block={{ ...block, items: block.steps || [] }} isMobile={isMobile} />;

      case 'cloze_text':
        return <ClozeTextRenderer key={key} block={block} isMobile={isMobile} />;

      case 'outline':
        return <OutlineRenderer key={key} block={block} isMobile={isMobile} />;

      case 'flashcards':
        return <FlashcardsRenderer key={key} block={block} isMobile={isMobile} />;

      case 'tips_grid':
        return <TipsGridRenderer key={key} block={block} isMobile={isMobile} />;

      case 'example':
        return <ExampleRenderer key={key} block={block} isMobile={isMobile} />;

      case 'warning':
        return <WarningRenderer key={key} block={block} isMobile={isMobile} />;

      case 'video':
        return <VideoRenderer key={key} block={block} isMobile={isMobile} />;

        default:
           // Keep the error handling for unknown types or rendering errors
           // console.warn(`⚠️ Unknown block type: "${block.type}"`, block); // Optionally hide console warning
           return null; // Return null instead of error box if you prefer silence

      }
    } catch (error) {
      console.error(`❌ Error rendering block type "${block.type}":`, error, block);
      return (
        <div key={key} style={{
          padding: '1rem',
          background: '#FEE2E2', // Light red background for errors
          border: '2px solid #EF4444', // Red border
          borderRadius: '0.5rem',
          marginBottom: '1rem'
        }}>
          <p style={{ fontWeight: '600', color: '#991B1B', marginBottom: '0.5rem' }}>
            ❌ Error rendering block type: "{block.type}"
          </p>
          <p style={{ fontSize: '0.875rem', color: '#7F1D1D' }}>
            {error.message}
          </p>
        </div>
      );
    }
  };

  // ... (Keep handleSectionComplete logic) ...
  const handleSectionComplete = (sectionIndex) => {
    const newCompleted = new Set(completedSections);
    newCompleted.add(sectionIndex);
    setCompletedSections(newCompleted);

    if (sectionIndex < sections.length - 1) {
      const next = sectionIndex + 1;
      if (isControlled && onSectionChange) {
        setTimeout(() => onSectionChange(next, sections.length), 300);
      } else {
        setTimeout(() => {
          setInternalSection(next);
          setInternalExpanded(next);
        }, 300);
      }
    } else if (onComplete) {
      onComplete();
    }
  };


  const progressPercentage = sections.length > 0 ? (completedSections.size / sections.length) * 100 : 0;

  // Styles - UPDATED sectionNav, sectionNavItem to match ProgressiveSectionReveal
  const styles = {
    container: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? 0 : '1.5rem', // Added gap for desktop consistency
      marginTop: 0,
      padding: 0,
    },
    sidebar: {
      width: isMobile ? '100%' : '280px',
      flexShrink: 0,
      paddingRight: isMobile ? 0 : '1rem', // Space between sidebar and content
    },
    progressBar: { // Style matches ProgressiveSectionReveal; tighter on mobile
      background: '#FFFFFF',
      backgroundImage: `
        linear-gradient(0deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent),
        linear-gradient(90deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent)
      `,
      backgroundSize: '80px 80px',
      borderRadius: isMobile ? '0.5rem' : '0.75rem',
      padding: isMobile ? '0.75rem 1rem' : '1.5rem',
      marginBottom: isMobile ? '0.75rem' : '1rem',
      border: '2px solid #E5E7EB',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    },
    progressTitle: { // Style matches ProgressiveSectionReveal; smaller on mobile
      fontSize: isMobile ? '0.75rem' : '0.875rem',
      fontWeight: '600',
      color: '#6b7280',
      marginBottom: isMobile ? '0.5rem' : '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    progressTrack: { // Style matches ProgressiveSectionReveal
      height: '8px',
      background: '#e5e7eb', // Light gray track
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '0.5rem'
    },
    progressFill: { // Style matches ProgressiveSectionReveal
      height: '100%',
      background: 'linear-gradient(90deg, #000000 0%, #4a4a4a 100%)', // Black gradient fill
      borderRadius: '4px',
      transition: 'width 0.5s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    progressText: { // Style matches ProgressiveSectionReveal; smaller on mobile
      fontSize: isMobile ? '0.8125rem' : '0.875rem',
      color: '#374151'
    },
    // --- UPDATED Styles for Navigation ---
    sectionNav: { // Style matches ProgressiveSectionReveal
      background: '#FFFFFF',
      backgroundImage: `
        linear-gradient(0deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent),
        linear-gradient(90deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent)
      `,
      backgroundSize: '80px 80px',
      borderRadius: '0.75rem',
      padding: '0.5rem',
      border: '2px solid #E5E7EB', // Lighter border
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)' // Softer shadow
    },
    sectionNavItem: { // Tighter on mobile for panel/sidebar
      display: 'flex',
      alignItems: 'center',
      padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
      marginBottom: isMobile ? '0.2rem' : '0.25rem',
      borderRadius: isMobile ? '0.375rem' : '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
      overflow: 'hidden'
    },
    sectionNumber: { // Slightly smaller on mobile
      width: isMobile ? '24px' : '28px',
      height: isMobile ? '24px' : '28px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: isMobile ? '0.5rem' : '0.75rem',
      fontSize: isMobile ? '0.75rem' : '0.875rem',
      fontWeight: '600',
      transition: 'all 0.3s ease'
    },
    sectionTitle: { // Readable on mobile; more titles fit in panel
      flex: 1,
      fontSize: isMobile ? '0.8125rem' : '0.875rem',
      fontWeight: '500',
      transition: 'color 0.2s ease',
      position: 'relative',
      zIndex: 1,
      lineHeight: 1.25
    },
    // --- End of Updated Styles ---
    content: {
      flex: 1,
      minWidth: 0,
      padding: 0,
    },
    sectionContent: { // Mobile: use edges (tighter padding); desktop unchanged
      background: '#FFFFFF',
      backgroundImage: `
        linear-gradient(0deg, transparent 24%, rgba(0, 0, 0, .01) 25%, rgba(0, 0, 0, .01) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .01) 75%, rgba(0, 0, 0, .01) 76%, transparent 77%, transparent),
        linear-gradient(90deg, transparent 24%, rgba(0, 0, 0, .01) 25%, rgba(0, 0, 0, .01) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .01) 75%, rgba(0, 0, 0, .01) 76%, transparent 77%, transparent)
      `,
      backgroundSize: '80px 80px',
      borderRadius: isMobile ? '0.5rem' : '1rem',
      padding: isMobile ? '0.5rem 0.75rem' : '2rem',
      minHeight: 'auto',
      display: 'flex',
      flexDirection: 'column',
      margin: 0,
      width: '100%',
      boxSizing: 'border-box',
      border: isMobile ? 'none' : '2px solid rgba(0, 0, 0, 0.1)',
      boxShadow: isMobile ? 'none' : '0 4px 15px rgba(0, 0, 0, 0.05)',
      marginBottom: isMobile ? '0.5rem' : '1rem',
      opacity: 0,
      animation: 'fadeInUp 0.6s ease-out forwards'
    },
    sectionHeader: { // Tighter on mobile for more content space
      display: 'flex',
      alignItems: 'center',
      marginBottom: isMobile ? '0.75rem' : '1.5rem',
      paddingBottom: isMobile ? '0.5rem' : '1rem',
      borderBottom: '2px solid #f3f4f6'
    },
    continueButton: { // Style matches ProgressiveSectionReveal
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.75rem',
      background: '#000000', // Solid black button
      color: 'white',
      border: '3px solid #000000',
      padding: isMobile ? '0.75rem 1.5rem' : '1rem 2rem',
      borderRadius: '1rem', // Rounded button
      fontSize: isMobile ? '1rem' : '1.125rem',
      fontWeight: '700', // Bold text
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', // Bouncy transition
      marginTop: isMobile ? '1rem' : '1.5rem',
      alignSelf: isMobile ? 'center' : 'flex-start',
      width: 'auto',
      position: 'relative',
      overflow: 'hidden', // For animations
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)', // Subtle shadow
    },
    mobileProgress: { // Compact on mobile
      position: isMobile ? 'sticky' : 'relative',
      top: isMobile ? '0' : 'auto',
      background: 'white',
      zIndex: 10,
      padding: isMobile ? '0' : '0',
      marginBottom: isMobile ? '0.5rem' : '1rem',
      boxShadow: isMobile ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
    }
  };

  // --- UPDATED Style Functions to match ProgressiveSectionReveal ---
  const getNavItemStyle = (index) => {
    const isActive = index === expandedSection;
    const isCompleted = completedSections.has(index);
    const isAccessible = index <= currentSection;

    return {
      ...styles.sectionNavItem,
      background: isActive ? '#000000' : // Active is BLACK
                  isCompleted ? '#FFFFFF' : // Completed is WHITE
                  isAccessible ? '#F9FAFB' : '#F9FAFB', // Default/Accessible is light gray
      border: isActive ? '2px solid #000000' :
              isCompleted ? '2px solid #000000' : // Completed gets BLACK border
              '2px solid #E5E7EB', // Default is light gray border
      cursor: isAccessible ? 'pointer' : 'not-allowed',
      opacity: isAccessible ? 1 : 0.5, // Dim inaccessible items
      boxShadow: isActive ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none', // Add shadow to active
    };
  };

  const getNumberStyle = (index) => {
    const isActive = index === expandedSection;
    const isCompleted = completedSections.has(index);
    const isAccessible = index <= currentSection;

    return {
      ...styles.sectionNumber,
      background: isActive ? 'rgba(255,255,255,0.2)' : // Active number bg on black
                  isCompleted ? '#000000' : // Completed number bg is BLACK
                  '#E5E7EB', // Default number bg is gray
      color: isActive ? 'white' :
             isCompleted ? 'white' : // Completed number text is WHITE (Check icon)
             isAccessible ? '#000000' : '#9CA3AF' // Default number text is black, inaccessible gray
    };
  };

  const getTitleStyle = (index) => {
    const isActive = index === expandedSection;
    const isCompleted = completedSections.has(index);

    return {
      ...styles.sectionTitle,
      color: isActive ? 'white' : // Active title is WHITE
             isCompleted ? '#000000' : // Completed title is BLACK
             '#374151', // Default title is dark gray
      fontWeight: isActive ? '600' : isCompleted ? '600' : '500', // Bolder active/completed
    };
  };
  // --- End of Updated Style Functions ---

  if (!sections || sections.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
        {t('lesson.error.noContent')}
      </div>
    );
  }

  return (
    <>
      {/* Mobile slide-out Sections menu with slide animation */}
      {isMobile && (isSectionsMenuOpen || isSectionsMenuClosing) && (
        <>
          <div
            onClick={closeSectionsMenu}
            className={`sections-backdrop ${isSectionsMenuClosing ? 'sections-backdrop--closing' : ''}`}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
          <div
            className={`sections-panel ${isSectionsMenuClosing ? 'sections-panel--closing' : 'sections-panel--open'}`}
            style={{
              position: 'fixed',
              top: 0, left: 0, bottom: 0,
              width: '280px',
              maxWidth: '85vw',
              background: 'white',
              zIndex: 1000,
              overflowY: 'auto',
              boxShadow: '4px 0 24px rgba(0,0,0,0.18)',
              padding: '0.75rem',
              paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
              paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left))',
            }}
          >
            <button
              onClick={closeSectionsMenu}
              style={{
                position: 'absolute', top: '1rem', right: '1rem',
                background: 'transparent', border: 'none', fontSize: '1.5rem',
                cursor: 'pointer', color: '#6b7280', padding: '0.5rem'
              }}
              aria-label="Close"
            >
              ×
            </button>
            <div style={{ ...styles.progressBar, marginTop: '2rem' }}>
              <div style={styles.progressTitle}>{t('grammar.progress.title')}</div>
              <div style={styles.progressTrack}>
                <div style={{ ...styles.progressFill, width: `${progressPercentage}%` }} />
              </div>
              <div style={styles.progressText}>
                {completedSections.size} / {sections.length} {t('vocabulary.message.sessionComplete')}
              </div>
            </div>
            <div style={styles.sectionNav}>
              {sections.map((section, index) => {
                const isAccessible = index <= currentSection;
                return (
                  <div
                    key={index}
                    style={{ ...getNavItemStyle(index), position: 'relative', overflow: 'hidden' }}
                    onClick={() => {
                      if (!isAccessible) return;
                      if (isControlled && onSectionChange) onSectionChange(index, sections.length);
                      else setInternalExpanded(index);
                      closeSectionsMenu();
                    }}
                    className="section-nav-item"
                  >
                    <div style={getNumberStyle(index)}>
                      {completedSections.has(index) ? <Check size={16} /> : index + 1}
                    </div>
                    <div style={getTitleStyle(index)}>{section.title}</div>
                    {index === expandedSection && <ChevronRight size={18} color="white" />}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div style={styles.container}>
        {/* LEFT SIDEBAR - Desktop / TOP BAR - Mobile */}
        <div style={styles.sidebar}>
          {/* Progress Bar */}
          <div style={{ ...styles.progressBar, ...(isMobile ? styles.mobileProgress : {}) }}>
            <div style={styles.progressTitle}>
              {t('grammar.progress.title')}
            </div>
            <div style={styles.progressTrack}>
              <div
                style={{
                  ...styles.progressFill,
                  width: `${progressPercentage}%`
                }}
              />
            </div>
            <div style={styles.progressText}>
              {completedSections.size} / {sections.length} {t('vocabulary.message.sessionComplete')}
            </div>
          </div>

          {/* Section Navigation */}
          {!isMobile && ( // Only render nav directly in sidebar on desktop
            <div style={styles.sectionNav}>
              {sections.map((section, index) => {
                const isActive = index === expandedSection;
                const isCompleted = completedSections.has(index);
                const isAccessible = index <= currentSection;

                return (
                  // --- ADDED wrapper div with class for hover effects ---
                  <div
                    key={index}
                    style={{
                      ...getNavItemStyle(index),
                      // Inline styles needed for animations if not using CSS classes everywhere
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onClick={() => {
                      if (!isAccessible) return;
                      if (isControlled && onSectionChange) onSectionChange(index, sections.length);
                      else setInternalExpanded(index);
                    }}
                    className="section-nav-item" // Add class for CSS targeting
                  >
                    {/* Decorative dots (hidden by default, shown via CSS) */}
                    <div style={{
                      position: 'absolute', top: '8px', left: '8px', width: '6px', height: '6px',
                      borderRadius: '50%', backgroundColor: '#3A86FF', opacity: 0,
                      transition: 'opacity 0.3s ease', pointerEvents: 'none'
                    }} className="nav-dot-1" />
                    <div style={{
                      position: 'absolute', top: '8px', right: '8px', width: '6px', height: '6px',
                      borderRadius: '50%', backgroundColor: '#3A86FF', opacity: 0,
                      transition: 'opacity 0.3s ease 0.1s', pointerEvents: 'none'
                    }} className="nav-dot-2" />
                    <div style={{
                       position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)',
                       width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#3A86FF',
                       opacity: 0, transition: 'opacity 0.3s ease 0.2s', pointerEvents: 'none'
                    }} className="nav-dot-3" />

                    {/* Shine effect (hidden by default, shown via CSS) */}
                    <div style={{
                      position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%',
                      background: 'linear-gradient(45deg, transparent, rgba(58, 134, 255, 0.1), transparent)',
                      transform: 'translateX(-100%)', transition: 'transform 0.6s ease', pointerEvents: 'none'
                    }} className="nav-shine" />

                    {/* Original Content */}
                    <div style={getNumberStyle(index)}>
                      {isCompleted ? <Check size={16} /> : index + 1}
                    </div>
                    <div style={getTitleStyle(index)}>
                      {section.title}
                    </div>
                    {isActive && <ChevronRight size={18} color="white" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* MAIN CONTENT AREA */}
        <div style={styles.content}>
          {sections.map((section, index) => (
            index === expandedSection && index <= currentSection && (
              <div key={index} style={styles.sectionContent}>
                {/* Section Header */}
                <div style={styles.sectionHeader}>
                  <div style={{
                    ...styles.sectionNumber,
                    background: '#000000',
                    color: 'white',
                    marginRight: isMobile ? '0.5rem' : '1rem'
                  }}>
                    {index + 1}
                  </div>
                  <h3 style={{
                    fontSize: isMobile ? '0.9375rem' : '1.5rem',
                    fontWeight: '600',
                    color: '#1f2937',
                    lineHeight: 1.3,
                    flex: 1,
                    minWidth: 0
                  }}>
                    {section.title}
                  </h3>
                </div>

                {/* Section Content - Render all blocks */}
                <div style={{ flex: 1 }}>
                  {section.blocks && section.blocks.length > 0 ? (
                    <>
                      {console.log(`📄 Rendering section "${section.title}" with ${section.blocks.length} blocks:`, section.blocks.map(b => b.type))}
                      {section.blocks.map((block, blockIndex) =>
                        renderBlock(block, blockIndex)
                      )}
                    </>
                  ) : (
                    // Keep the "No content" warning box
                    <div style={{ padding: '2rem', textAlign: 'center', background: '#FEF3C7', border: '2px solid #F59E0B', borderRadius: '0.75rem', marginBottom: '1rem' }}>
                      <p style={{ color: '#92400E', fontWeight: '600', marginBottom: '0.5rem' }}>⚠️ No content in this section</p>
                      <p style={{ color: '#78350F', fontSize: '0.875rem' }}>This section exists but has no content blocks.</p>
                    </div>
                  )}
                </div>

                {/* Continue Button (non-last sections) */}
                {index <= currentSection && index !== sections.length - 1 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                    <button
                      style={styles.continueButton}
                      onClick={() => handleSectionComplete(index)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                      className="continue-button-animated"
                    >
                      <div className="shimmer-effect" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', backgroundSize: '200% 100%', opacity: 0, transition: 'opacity 0.3s', pointerEvents: 'none' }} />
                      <div className="particle-container" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                        {[...Array(12)].map((_, i) => ( <div key={i} className="particle" style={{ position: 'absolute', width: '6px', height: '6px', background: 'white', borderRadius: '50%', left: '50%', top: '50%', opacity: 0 }} /> ))}
                      </div>
                      <div className="corner-top-left" style={{ position: 'absolute', top: '4px', left: '4px', width: '12px', height: '12px', borderTop: '2px solid white', borderLeft: '2px solid white', opacity: 0, transition: 'opacity 0.3s' }} />
                      <div className="corner-bottom-right" style={{ position: 'absolute', bottom: '4px', right: '4px', width: '12px', height: '12px', borderBottom: '2px solid white', borderRight: '2px solid white', opacity: 0, transition: 'opacity 0.3s' }} />
                      <span style={{ position: 'relative', zIndex: 1 }}>{t('button.continue')}</span>
                      <ChevronRight size={18} style={{ position: 'relative', zIndex: 1 }} />
                    </button>
                  </div>
                )}

                {/* Finish lesson button (last section only) */}
                {index <= currentSection && index === sections.length - 1 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                    <button
                      style={styles.continueButton}
                      onClick={() => handleSectionComplete(index)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                      className="continue-button-animated"
                    >
                      <div className="shimmer-effect" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', backgroundSize: '200% 100%', opacity: 0, transition: 'opacity 0.3s', pointerEvents: 'none' }} />
                      <div className="corner-top-left" style={{ position: 'absolute', top: '4px', left: '4px', width: '12px', height: '12px', borderTop: '2px solid white', borderLeft: '2px solid white', opacity: 0, transition: 'opacity 0.3s' }} />
                      <div className="corner-bottom-right" style={{ position: 'absolute', bottom: '4px', right: '4px', width: '12px', height: '12px', borderBottom: '2px solid white', borderRight: '2px solid white', opacity: 0, transition: 'opacity 0.3s' }} />
                      <CheckCircle size={20} style={{ position: 'relative', zIndex: 1, flexShrink: 0 }} />
                      <span style={{ position: 'relative', zIndex: 1 }}>{t('lesson.finish', { defaultValue: 'Finish lesson' })}</span>
                    </button>
                  </div>
                )}
              </div>
            )
          ))}
        </div>
      </div>

      {/* --- ADDED Style block with animations from ProgressiveSectionReveal --- */}
      <style>{`
        /* Sections side panel: slide in/out */
        @keyframes sectionsSlideIn {
          from {
            opacity: 0;
            transform: translateX(-100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes sectionsSlideOut {
          from {
            opacity: 1;
            transform: translateX(0);
          }
          to {
            opacity: 0.6;
            transform: translateX(-100%);
          }
        }
        @keyframes sectionsBackdropIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes sectionsBackdropOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        .sections-panel--open {
          animation: sectionsSlideIn 0.32s cubic-bezier(0.32, 0.72, 0, 1) forwards;
        }
        .sections-panel--closing {
          animation: sectionsSlideOut 0.28s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards;
        }
        .sections-backdrop:not(.sections-backdrop--closing) {
          animation: sectionsBackdropIn 0.25s ease-out forwards;
        }
        .sections-backdrop--closing {
          animation: sectionsBackdropOut 0.25s ease-in forwards;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInFromLeft {
          from { opacity: 0; transform: translateX(-100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes particleFloat {
          0% { transform: translate(-50%, -50%) scale(0); opacity: 1; }
          100% {
            transform: translate(
              calc(-50% + var(--tx, 0px)),
              calc(-50% + var(--ty, 0px))
            ) scale(1);
            opacity: 0;
          }
        }

        /* Section Navigation Hover Effects */
        .section-nav-item:hover .nav-dot-1,
        .section-nav-item:hover .nav-dot-2,
        .section-nav-item:hover .nav-dot-3 {
          opacity: 1 !important;
        }
        .section-nav-item:hover .nav-shine {
          transform: translateX(100%) !important;
        }
        .section-nav-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(58, 134, 255, 0.15) !important; /* Blue shadow on hover */
        }

        /* Continue Button Animations */
        .continue-button-animated:hover .shimmer-effect {
          opacity: 1;
          animation: shimmer 1.5s infinite linear; /* Ensure linear animation */
        }
        .continue-button-animated:hover .corner-top-left,
        .continue-button-animated:hover .corner-bottom-right {
          opacity: 1;
        }
        .continue-button-animated:hover .particle {
          animation: particleFloat 0.8s ease-out forwards;
        }

        /* Define particle delays and positions */
        .continue-button-animated .particle:nth-child(1) { animation-delay: 0s; --tx: 60px; --ty: 0px; }
        .continue-button-animated .particle:nth-child(2) { animation-delay: 0.05s; --tx: 52px; --ty: 30px; }
        .continue-button-animated .particle:nth-child(3) { animation-delay: 0.1s; --tx: 30px; --ty: 52px; }
        .continue-button-animated .particle:nth-child(4) { animation-delay: 0.15s; --tx: 0px; --ty: 60px; }
        .continue-button-animated .particle:nth-child(5) { animation-delay: 0.2s; --tx: -30px; --ty: 52px; }
        .continue-button-animated .particle:nth-child(6) { animation-delay: 0.25s; --tx: -52px; --ty: 30px; }
        .continue-button-animated .particle:nth-child(7) { animation-delay: 0.3s; --tx: -60px; --ty: 0px; }
        .continue-button-animated .particle:nth-child(8) { animation-delay: 0.35s; --tx: -52px; --ty: -30px; }
        .continue-button-animated .particle:nth-child(9) { animation-delay: 0.4s; --tx: -30px; --ty: -52px; }
        .continue-button-animated .particle:nth-child(10) { animation-delay: 0.45s; --tx: 0px; --ty: -60px; }
        .continue-button-animated .particle:nth-child(11) { animation-delay: 0.5s; --tx: 30px; --ty: -52px; }
        .continue-button-animated .particle:nth-child(12) { animation-delay: 0.55s; --tx: 52px; --ty: -30px; }

      `}</style>
    </>
  );
};

export default LessonContentRenderer;